import React, { useState, useEffect, useRef } from 'react';
import '../App.css';

function ServoControl({ bluetoothDevice, usbPort, sendCommand, autoTracking, setAutoTracking, headPose, cameraRunning, gazeData }) {
  const [panAngle, setPanAngle] = useState(90);
  const [tiltAngle, setTiltAngle] = useState(90);

  // Throttling for auto-tracking
  const lastSentAngles = useRef({ pan: null, tilt: null });
  const lastSendTime = useRef(0);
  const isSending = useRef(false);
  const UPDATE_INTERVAL = 2000; // Super slow mode: 2 seconds between updates

  const handlePanChange = (e) => {
    const angle = parseInt(e.target.value);
    setPanAngle(angle);
  };

  const handleTiltChange = (e) => {
    const angle = parseInt(e.target.value);
    setTiltAngle(angle);
  };

  const sendPanCommand = async () => {
    if (!bluetoothDevice?.gatt?.connected && !usbPort) {
      alert('Please connect to Bluetooth or USB device first');
      return;
    }
    await sendCommand(`pan ${panAngle}`);
  };

  const sendTiltCommand = async () => {
    if (!bluetoothDevice?.gatt?.connected && !usbPort) {
      alert('Please connect to Bluetooth or USB device first');
      return;
    }
    await sendCommand(`tilt ${tiltAngle}`);
  };

  const sendBothCommands = async () => {
    if (!bluetoothDevice?.gatt?.connected && !usbPort) {
      alert('Please connect to Bluetooth or USB device first');
      return;
    }
    await sendCommand(`pan ${panAngle}`);
    await new Promise(resolve => setTimeout(resolve, 500));
    await sendCommand(`tilt ${tiltAngle}`);
  };

  const centerServos = async () => {
    setPanAngle(90);
    setTiltAngle(90);
    await sendBothCommands();
  };

  // Auto-tracking: use calculated light angles to point at gaze target
  useEffect(() => {
    if (!autoTracking) return;

    const isBluetoothConnected = bluetoothDevice?.gatt?.connected;
    const isUsbConnected = !!usbPort;

    if (!isBluetoothConnected && !isUsbConnected) return;

    const now = Date.now();

    // Only send if enough time has passed (throttle to 2 seconds)
    if (now - lastSendTime.current < UPDATE_INTERVAL) {
      return;
    }

    // Use calculated light angles from backend (more accurate)
    let panAngle, tiltAngle;

    if (gazeData && gazeData.lightAngles) {
      // Use gaze-based calculation (points light where person is looking)
      panAngle = Math.round(gazeData.lightAngles.pan);
      tiltAngle = Math.round(gazeData.lightAngles.tilt);
    } else {
      // Fallback: simple head pose mapping
      panAngle = Math.round(90 + (headPose.yaw * 2));
      tiltAngle = Math.round(90 - (headPose.pitch * 2));
    }

    // Constrain to valid servo range
    // Pan: 0-180 (0 = right, 90 = center, 180 = left when looking at laptop)
    // Tilt: 0-153 (0 = up, 90 = center, 153 = max down)
    const constrainedPan = Math.max(0, Math.min(180, panAngle));
    const constrainedTilt = Math.max(0, Math.min(153, tiltAngle)); // Max tilt = 153

    // Only send if angles changed significantly (more than 5 degrees) or first time
    const panChanged = lastSentAngles.current.pan === null ||
      Math.abs(constrainedPan - lastSentAngles.current.pan) > 5;
    const tiltChanged = lastSentAngles.current.tilt === null ||
      Math.abs(constrainedTilt - lastSentAngles.current.tilt) > 5;

    // Send servo commands if changed
    if ((panChanged || tiltChanged) && !isSending.current) {
      const sendSequence = async () => {
        isSending.current = true;
        try {
          if (panChanged) {
            await sendCommand(`pan ${constrainedPan}`);
            lastSentAngles.current.pan = constrainedPan;
          }

          // Wait longer between commands (500ms)
          if (panChanged && tiltChanged) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          if (tiltChanged) {
            await sendCommand(`tilt ${constrainedTilt}`);
            lastSentAngles.current.tilt = constrainedTilt;
          }

          lastSendTime.current = Date.now();
        } catch (error) {
          console.error("Auto-tracking command failed:", error);
        } finally {
          isSending.current = false;
        }
      };

      sendSequence();
    }
  }, [headPose, autoTracking, bluetoothDevice, gazeData, sendCommand, usbPort]);

  return (
    <div className="card">
      <h2>🎮 Servo Control</h2>

      <div className="input-group" style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '15px',
          background: autoTracking ? 'rgba(76, 175, 80, 0.2)' : 'rgba(33, 150, 243, 0.2)',
          borderRadius: '10px',
          border: `2px solid ${autoTracking ? '#4caf50' : '#2196f3'}`
        }}>
          <div>
            <label style={{ fontSize: '1.2em', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
              {autoTracking ? '🤖 Auto Tracking Mode' : '🎮 Manual Control Mode'}
            </label>
            <p style={{ fontSize: '0.9em', opacity: 0.9, margin: 0 }}>
              {!cameraRunning
                ? '⚠️ Camera must be running for auto tracking'
                : autoTracking
                  ? 'Servos automatically follow head movement'
                  : 'Use sliders to manually control servos'}
            </p>
          </div>
          <button
            className={`button ${autoTracking ? 'danger' : 'success'}`}
            onClick={() => setAutoTracking(!autoTracking)}
            disabled={!cameraRunning}
            style={{
              minWidth: '150px',
              fontSize: '1em',
              padding: '12px 20px',
              opacity: cameraRunning ? 1 : 0.5
            }}
          >
            {autoTracking ? 'Switch to Manual' : 'Switch to Auto'}
          </button>
        </div>
      </div>

      {!autoTracking && (
        <>
          <div className="input-group">
            <label>Pan Angle: {panAngle}°</label>
            <input
              type="range"
              min="0"
              max="180"
              value={panAngle}
              onChange={handlePanChange}
              className="slider"
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button className="button" onClick={sendPanCommand}>
                Set Pan
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Tilt Angle: {tiltAngle}° (Max: 153°)</label>
            <input
              type="range"
              min="0"
              max="153"
              value={tiltAngle}
              onChange={handleTiltChange}
              className="slider"
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button className="button" onClick={sendTiltCommand}>
                Set Tilt
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button className="button success" onClick={sendBothCommands}>
              Set Both
            </button>
            <button className="button" onClick={centerServos}>
              Center (90°)
            </button>
          </div>
        </>
      )}

      {autoTracking && (
        <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(76, 175, 80, 0.2)', borderRadius: '10px' }}>
          <h3>Auto Tracking Active</h3>
          <p style={{ marginTop: '10px', lineHeight: '1.6' }}>
            <strong>Gaze Tracking:</strong><br />
            Distance: {gazeData.distance ? gazeData.distance.toFixed(2) : 'N/A'}m<br />
            Target: ({gazeData.targetPoint?.x?.toFixed(2) || '0'}, {gazeData.targetPoint?.y?.toFixed(2) || '0'}, {gazeData.targetPoint?.z?.toFixed(2) || '0'})m<br />
            <strong>Light Angles:</strong><br />
            Pan: {gazeData.lightAngles?.pan?.toFixed(1) || '90'}°<br />
            Tilt: {gazeData.lightAngles?.tilt?.toFixed(1) || '90'}°
          </p>
        </div>
      )}
    </div>
  );
}

export default ServoControl;
